import { FilePlus, Trash, Article as ArticleIcon, Sparkle, ImageSquare } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import GenerationWizard from "@/components/studio/GenerationWizard";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const wordCount = (s) => (s ? s.trim().split(/\s+/).filter(Boolean).length : 0);

const articleWordCount = (a) => {
  if (a.parts && a.parts.length > 0)
    return a.parts.reduce((sum, p) => sum + wordCount(p.body), 0);
  return wordCount(a.content);
};

const statusLabel = (a) => {
  if (a.status === "imaged") return "GÖRSEL HAZIR";
  if (a.status === "scenes") return "SAHNE HAZIR";
  if (a.status === "parts") return "MAKALE HAZIR";
  if (a.parts && a.parts.length) return "MAKALE HAZIR";
  return "TASLAK";
};

export const ContentPanel = ({
  channel,
  articles,
  activeArticle,
  onOpenArticle,
  onNewBlank,
  onSaveMeta,
  onArticleUpdated,
  onDelete,
  onCloseEditor,
  onStartProcessing,
}) => {
  if (!channel) return <NoChannelState />;

  if (activeArticle) {
    return (
      <GenerationWizard
        article={activeArticle}
        onBack={onCloseEditor}
        onDelete={onDelete}
        onSaveMeta={onSaveMeta}
        onArticleUpdated={onArticleUpdated}
        onStartProcessing={onStartProcessing}
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-background" data-testid="content-panel">
      <div className="p-6 border-b border-border/60 flex items-center justify-between">
        <div>
          <span className="label-mono text-muted-foreground">// channel/{channel.language}</span>
          <h2 className="heading-font font-black text-3xl tracking-tight mt-1">
            {channel.name}
          </h2>
          <p className="label-mono text-muted-foreground mt-2">
            {channel.category} · {String(articles.length).padStart(2, "0")} article
            {articles.length === 1 ? "" : "s"}
          </p>
        </div>
        <Button
          data-testid="new-blank-article-btn"
          onClick={onNewBlank}
          className="rounded-none h-11 bg-white text-black hover:bg-[#EAEAEA] font-bold uppercase tracking-wider text-xs transition-colors"
        >
          <FilePlus size={16} weight="bold" className="mr-2" />
          Boş Makale
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {articles.length === 0 ? (
          <EmptyContentState />
        ) : (
          <ul data-testid="article-list">
            {articles.map((a) => {
              const label = statusLabel(a);
              const isImaged = a.status === "imaged";
              return (
                <li
                  key={a.id}
                  data-testid={`article-item-${a.id}`}
                  onClick={() => onOpenArticle(a)}
                  className="group px-6 py-5 border-b border-border/40 cursor-pointer hover:bg-[#111] transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className={`label-mono border px-1.5 py-0.5 ${
                            isImaged
                              ? "border-primary text-primary"
                              : a.parts?.length
                                ? "border-white text-white"
                                : "border-border text-muted-foreground"
                          }`}
                        >
                          {label}
                        </span>
                        {a.duration && (
                          <span className="label-mono text-muted-foreground">
                            {a.duration} DK
                          </span>
                        )}
                        {a.scenes?.length > 0 && (
                          <span className="label-mono text-muted-foreground flex items-center gap-1">
                            <ImageSquare size={11} />
                            {a.scenes.filter((s) => s.image_url).length}/{a.scenes.length}
                          </span>
                        )}
                      </div>
                      <p className="heading-font font-bold text-[17px] tracking-tight truncate">
                        {a.title}
                      </p>
                      {a.title_tr && (
                        <p className="heading-font text-[13px] text-muted-foreground truncate">
                          TR: {a.title_tr}
                        </p>
                      )}
                      {a.parts?.length > 0 ? (
                        <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">
                          {a.parts[0]?.body?.slice(0, 180)}…
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">
                          {a.content || "İçerik yok — düzenlemek için tıkla."}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-3">
                        <span className="label-mono text-muted-foreground">
                          {formatDate(a.updated_at)}
                        </span>
                        <span className="label-mono text-muted-foreground">
                          {articleWordCount(a)} words
                        </span>
                      </div>
                    </div>
                    <button
                      data-testid={`article-delete-${a.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`"${a.title}" silinsin mi?`)) onDelete(a.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 h-8 w-8 flex items-center justify-center border border-border hover:border-primary hover:text-primary text-muted-foreground transition-colors"
                    >
                      <Trash size={13} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const NoChannelState = () => (
  <div
    className="h-full flex flex-col items-center justify-center bg-background"
    data-testid="no-channel-state"
  >
    <div className="font-mono text-[10px] text-muted-foreground leading-tight whitespace-pre">
{`┌───────────────────────────────┐
│                               │
│    [  N O   S I G N A L  ]    │
│                               │
│    Kanal seç veya oluştur     │
│                               │
└───────────────────────────────┘`}
    </div>
    <p className="label-mono text-muted-foreground mt-6">
      Soldan bir kanal seçerek başla
    </p>
  </div>
);

const EmptyContentState = () => (
  <div className="h-full flex flex-col items-center justify-center p-10 text-center">
    <div className="w-16 h-16 border border-border flex items-center justify-center mb-6">
      <ArticleIcon size={26} color="#737373" />
    </div>
    <h3 className="heading-font font-black text-xl tracking-tight text-white mb-2">
      Henüz makale yok
    </h3>
    <p className="text-sm text-muted-foreground max-w-sm">
      Sağdaki panelden AI ile başlık üret, sonra bir başlığa tıklayarak makale sihirbazını aç.
    </p>
    <div className="mt-6 flex items-center gap-2 text-xs">
      <Sparkle size={14} weight="fill" color="#FF3B30" />
      <span className="label-mono text-primary">→ AI Titles paneli</span>
    </div>
  </div>
);

export default ContentPanel;
